/**
 * Sun Wukong — „Battle of Legends, Volume Two".
 *
 * Klony (3 × 1 zdravie) nezačínajú na doske: `setup` ich odloží medzi
 * porazené (`state.defeated`) a vyvolanie je obyčajný návrat porazenej
 * figúrky (`placeFighter` — s plným zdravím). Porazený klon sa tak dá vyvolať znova.
 * Na začiatku ťahu smie Sun Wukong za 1 poškodenie vyvolať klona vedľa seba.
 */

import { areAdjacent, neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  cardDef,
  dealDamage,
  drawCard,
  emit,
  gainAction,
  getMap,
  getPlayer,
  handToDeckBottom,
  healFighter,
  placeFighter,
} from '../ops.js';
import { areAllies } from '../players.js';
import type { PlayerIndex, SpaceId } from '../types.js';
import { askBoost, emptySpacesInZone, fighterOrNull, opponentOf, revealHand } from './helpers.js';
import { wilyFighting } from './robinHood.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const WUKONG = 'sun-wukong';
const CLONE = 'clones';
const TAUNT = 'sun-wukong.taunting-laughter';
const SLY = 'sun-wukong.sly-monkey';
const FIERY = 'sun-wukong.fiery-eyes-that-see';
const FORMS = ['sun-wukong.ox-form', 'sun-wukong.tortoise-form', 'sun-wukong.phoenix-form'];

const wukongOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === WUKONG) ?? null;

/** Klon mimo dosky, ktorý sa dá vyvolať. */
const reserveClone = (ctx: Ctx, player: PlayerIndex) =>
  (ctx.state.defeated ?? []).find((f) => f.owner === player && f.defId === CLONE) ?? null;

/** Vyvolanie klona na daný priestor (musí byť prázdny). */
function summonClone(ctx: Ctx, player: PlayerIndex, spaceId: SpaceId): void {
  const clone = reserveClone(ctx, player);
  if (!clone || ctx.state.fighters.some((f) => f.spaceId === spaceId)) return;
  placeFighter(ctx, clone.id, spaceId);
}

/**
 * „For each other friendly fighter adjacent to the opposing fighter, increase
 * the value of this card by +1. AFTER: Gain 1 action."
 */
const infiniteStrikes: CardEffect = {
  during: (e, value) => {
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!them) return value;
    const map = getMap(e.ctx);
    const friends = e.ctx.state.fighters.filter(
      (f) => f.id !== e.fighterId && areAllies(e.ctx.state, f.owner, e.player) && areAdjacent(map, f.spaceId, them.spaceId),
    );
    return value + friends.length;
  },
  after: (e) => gainAction(e.ctx, e.player),
};

/** „If your opponent played a defense card, TRICKED YOU. TRICKED YOU: 4 ATK" — bonusový útok. */
const ruyiJingoBang: CardEffect = {
  after: (e) => {
    const defense = e.ctx.state.combat?.defenseCard;
    if (e.role !== 'attacker' || !defense || cardDef(e.ctx, defense).type !== 'defense') return;
    if (!e.fighterId || !e.opponentFighterId) return;
    askChoice(e.ctx, {
      kind: 'bonusAttack',
      player: e.player,
      source: e.cardId,
      label: 'Tricked You: bonusový útok za 4',
      optional: false,
      attackerFighter: e.fighterId,
      defenderFighter: e.opponentFighterId,
      name: 'Tricked You',
      value: 4,
    });
  },
};

/** „IMMEDIATELY: Your opponent may discard 1 card from their hand. If they don't, ignore their card's value." */
const tauntingLaughter: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    const opponent = e.opponentPlayer;
    const theirCard = e.role === 'defender' ? combat?.attackCard : combat?.defenseCard;
    if (!combat || opponent === null || !theirCard) return;
    const zero = e.role === 'defender' ? 'zeroAttack' : 'zeroDefense';
    if (getPlayer(e.ctx.state, opponent).hand.length === 0) {
      (combat.flags ??= []).push(zero);
      return;
    }
    askChoice(e.ctx, {
      kind: 'confirm',
      player: opponent,
      source: e.cardId,
      label: 'Taunting Laughter: odhodíš kartu z ruky? Inak sa hodnota tvojej karty ignoruje.',
      optional: true,
      effect: TAUNT,
      yes: 'Odhodiť kartu',
      no: 'Nie — hodnota 0',
      onSkip: { kind: 'effect', effect: TAUNT, data: zero },
    });
  },
  onConfirm: (ctx, choice) => {
    askChoice(ctx, {
      kind: 'card',
      player: choice.player,
      source: choice.source,
      label: 'Taunting Laughter: odhoď kartu',
      optional: false,
      from: 'hand',
      purpose: 'discard',
      discards: true,
    });
  },
  onFollow: (ctx, _choice, zero) => {
    const combat = ctx.state.combat;
    if (combat) (combat.flags ??= []).push(zero);
  },
};

/** Súper smie boostovať svoju kartu (Ox Form, Tortoise Form). */
function opponentMayBoost(e: EffectCtx, label: string): void {
  const combat = e.ctx.state.combat;
  if (!combat || e.opponentPlayer === null) return;
  const theirSide = e.role === 'defender' ? 'attacker' : 'defender';
  const theirCard = theirSide === 'attacker' ? (combat.bonus ? null : combat.attackCard) : combat.defenseCard;
  if (!theirCard) return;
  askBoost({ ...e, player: e.opponentPlayer, role: theirSide }, label);
}

/** „This card's effects cannot be canceled. DURING: If your opponent played a card, they may BOOST it." */
const oxForm: CardEffect = {
  uncancelable: true,
  duringChoice: (e) => opponentMayBoost(e, 'Ox Form: smieš boostovať svoju kartu'),
};

/** „Your opponent may BOOST their attack." */
const tortoiseForm: CardEffect = {
  duringChoice: (e) => opponentMayBoost(e, 'Tortoise Form: smieš boostovať svoj útok'),
};

/** „Take an OX FORM, TORTOISE FORM, or PHOENIX FORM from your discard pile and return it to your hand." */
const transformations: CardEffect = {
  after: (e) => {
    const cards = getPlayer(e.ctx.state, e.player).discard.filter((id) => FORMS.includes(e.ctx.state.cards[id]!.defId));
    if (cards.length === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: '72 Transformations: vezmi si formu z odhodu do ruky',
      optional: false,
      from: 'discard',
      cards,
      purpose: 'toHand',
    });
  },
};

/**
 * „If Sun Wukong played this card, summon a Clone in Sun Wukong's space, then
 * place Sun Wukong in a space in his zone." Najprv sa presunie Wukong, klon
 * príde na jeho pôvodné miesto — výsledok je ten istý, jeden priestor = jedna figúrka.
 */
const slyMonkey: CardEffect = {
  after: (e) => {
    const wukong = wukongOf(e.ctx, e.player);
    if (!wukong || e.fighterId !== wukong.id) return;
    const spaces = emptySpacesInZone(e.ctx, wukong.id);
    if (spaces.length === 0) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Sly Monkey: polož Sun Wukonga v jeho zóne (klon ostane na jeho mieste)',
      optional: false,
      fighterId: wukong.id,
      spaces,
    });
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Sly Monkey: klon',
      optional: false,
      follow: { kind: 'effect', effect: SLY, data: wukong.spaceId },
    });
  },
  onFollow: (ctx, choice, spaceId) => summonClone(ctx, choice.player, spaceId),
};

/** „DURING: Prevent all damage. AFTER: You may place your fighter in any space." */
export const bewilderment: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (combat) (combat.flags ??= []).push('damageCap:0');
  },
  after: (e) => {
    if (!e.fighterId) return;
    const occupied = new Set(e.ctx.state.fighters.map((f) => f.spaceId));
    const spaces = getMap(e.ctx).spaces.map((s) => s.id).filter((id) => !occupied.has(id));
    if (spaces.length === 0) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Bewilderment: smieš položiť figúrku na ľubovoľný priestor',
      optional: true,
      fighterId: e.fighterId,
      spaces,
    });
  },
};

/** „DURING: Any combat damage you would take is dealt to the opposing fighter instead." */
const goldenChainMail: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (combat && e.role === 'defender') (combat.flags ??= []).push('reflect');
  },
};

/**
 * „Look at an opponent's hand and choose a card. Put that card on the bottom
 * of their deck. You each draw 1 card."
 */
const fieryEyes: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    const bothDraw = { kind: 'effect' as const, effect: FIERY, data: String(opponent) };
    if (getPlayer(e.ctx.state, opponent).hand.length === 0 || !revealHand(e, opponent)) {
      askChoice(e.ctx, { kind: 'auto', player: e.player, source: e.cardId, label: 'Fiery Eyes', optional: false, follow: bothDraw });
      return;
    }
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Fiery Eyes That See: vyber kartu, ktorá pôjde pod súperov balíček',
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'effect',
      onSkip: bothDraw,
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    const owner = choice.owner ?? choice.player;
    handToDeckBottom(ctx, owner, cardId);
    drawCard(ctx, choice.player);
    drawCard(ctx, owner);
  },
  onFollow: (ctx, choice, opponent) => {
    drawCard(ctx, choice.player);
    drawCard(ctx, Number(opponent));
  },
};

/** „Sun Wukong recovers 1 health for each Clone on the board." */
const phoenixForm: CardEffect = {
  scheme: (e) => {
    const wukong = wukongOf(e.ctx, e.player);
    const clones = e.ctx.state.fighters.filter((f) => f.owner === e.player && f.defId === CLONE).length;
    if (wukong && clones > 0) healFighter(e.ctx, wukong.id, clones);
  },
};

export const sunWukongCards: EffectRegistry = {
  'sun-wukong.infinite-strikes': infiniteStrikes,
  'sun-wukong.ruyi-jingo-bang': ruyiJingoBang,
  [TAUNT]: tauntingLaughter,
  'sun-wukong.ox-form': oxForm,
  'sun-wukong.72-transformations': transformations,
  [SLY]: slyMonkey,
  'sun-wukong.wily-fighting': wilyFighting,
  'sun-wukong.bewilderment': bewilderment,
  'sun-wukong.golden-chain-mail': goldenChainMail,
  'sun-wukong.tortoise-form': tortoiseForm,
  [FIERY]: fieryEyes,
  'sun-wukong.phoenix-form': phoenixForm,
};

export const sunWukongSpecial: HeroSpecial = {
  name: 'Klony',
  /** „Do not start with any Clones on the board." */
  setup: (ctx, player) => {
    const clones = ctx.state.fighters.filter((f) => f.owner === player && f.defId === CLONE);
    if (clones.length === 0) return;
    ctx.state.fighters = ctx.state.fighters.filter((f) => !clones.includes(f));
    ctx.state.defeated = [...(ctx.state.defeated ?? []), ...clones.map((f) => ({ ...f, hp: 0 }))];
  },
  /** „At the start of your turn, you may take 1 damage to summon a Clone in an empty space adjacent to Sun Wukong." */
  startOfTurn: (ctx, player) => {
    const wukong = wukongOf(ctx, player);
    if (!wukong || !reserveClone(ctx, player)) return;
    const occupied = new Set(ctx.state.fighters.map((f) => f.spaceId));
    if (!neighbors(getMap(ctx), wukong.spaceId).some((id) => !occupied.has(id))) return;
    askChoice(ctx, {
      kind: 'confirm',
      player,
      source: null,
      label: 'Sun Wukong: za 1 poškodenie vyvolať klona vedľa seba?',
      optional: true,
      special: WUKONG,
      yes: 'Vyvolať klona (−1 zdravie)',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, player) => {
    const wukong = wukongOf(ctx, player);
    const clone = reserveClone(ctx, player);
    if (!wukong || !clone) return;
    dealDamage(ctx, wukong.id, 1);
    const occupied = new Set(ctx.state.fighters.map((f) => f.spaceId));
    const spaces = neighbors(getMap(ctx), wukong.spaceId).filter((id) => !occupied.has(id));
    if (spaces.length === 0) return;
    emit(ctx, { type: 'specialUsed', player, heroId: WUKONG, name: 'Vyvolanie klona' });
    askChoice(ctx, {
      kind: 'place',
      player,
      source: null,
      label: 'Sun Wukong: polož klona vedľa seba',
      optional: false,
      fighterId: clone.id,
      spaces,
    });
  },
};
